import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useUpdateBorrowerProfile } from '../../hooks/account_settings/useUpdateBorrowerProfile';
import { useDeleteDocuments } from '../../hooks/complete_profile/useDeleteDocuments';
import { useFetchBorrowerProfile } from '../../hooks/complete_profile/useFetchBorrowerProfile';
import FormError from '../Common/FormError';
import LoadingIndicator from '../Common/LoadingIndicator';
import LocationWithAddress from '../Common/LocationWithAddress';
import BorrowerBusiness from '../CompleteProfile/BorrowerBusinesss';
import BorrowerEmployer from '../CompleteProfile/BorrowerEmployer';

const BorrowerUpdateProfile = () => {
  const { profile, fetchBorrowerProfile } = useFetchBorrowerProfile();
  const { submitting, onSubmitHandler } = useUpdateBorrowerProfile();
  const { showWorkDocument, showIncomeDocument, deleteWorkDocument, deleteIncomeDocument } = useDeleteDocuments(fetchBorrowerProfile);

  const [isSelfEmployed, setEmploymentType] = useState<boolean>(profile?.is_self_employed ?? false);

  const handleSelectEmploymentType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEmploymentType(JSON.parse(e.target.value));
  };

  return profile ? (
    <Form
      initialValues={{
        address: profile.address,
        city: profile.address_city,
        state: profile.address_state_id,
        country: profile.address_country_id,
        occupation: profile.occupation,
        employment_type: profile.is_self_employed,
        employer_name: profile.employer_name,
        employer_city: profile.employer_city,
        employer_country: profile.employer_country_id,
        employer_state: profile.employer_state_id,
        job_title: profile.job_title,
        employment_duration: profile.duration,
        business_name: profile.business_name,
        business_city: profile.business_city,
        business_state: profile.business_state_id,
        business_country: profile.business_country_id,
        rc_no: profile.rc_no,
        business_type: profile.business_type,
        business_duration: profile.duration,
        linkedin: profile.linkedin,
        bvn: profile.bvn,
        nin: profile.nin,
      }}
      onSubmit={onSubmitHandler}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <h3 className='fw-bolder text-dark mb-5'>Personal Information</h3>
          <LocationWithAddress label='Address' addressFormName='address' cityFormName='city' stateFormName='state' countryFormName='country' countryId={profile?.address_country_id} readOnly={false} />

          <div className='row mt-5'>
            <div className='mb-5 col-lg-6'>
              <label className='form-label mb-3'>Occupation</label>
              <Field name='occupation' type='text'>
                {({ input, meta }) => (
                  <>
                    <div className='mb-3'>
                      <input type='text' {...input} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='mb-5 col-lg-6'>
              <label className='form-label mb-3'>Employment Type</label>
              <Field name='employment_type'>
                {({ input, meta }) => (
                  <>
                    <div className='mb-3'>
                      <select
                        {...input}
                        className='form-select form-select-lg form-select-solid'
                        onChange={(e) => {
                          input.onChange(e);
                          handleSelectEmploymentType(e);
                        }}>
                        <option value='true'>Self Employed</option>
                        <option value='false'>Employed</option>
                      </select>
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>
          </div>

          <h3 className='fw-bolder text-dark mb-5 mt-10'>Work Information</h3>
          {isSelfEmployed ? <BorrowerBusiness businessCountryId={profile.business_country_id} /> : <BorrowerEmployer employerCountryId={profile.employer_country_id} />}

          <h3 className='fw-bolder text-dark mb-5 mt-10'>Other Information</h3>
          <div className='mb-5 col-lg-12'>
            <Field name='linkedin' type='text' defaultValue={profile?.linkedin}>
              {({ input, meta }) => (
                <>
                  <label className='form-label'>Linkedin Profile</label>
                  <div className='mb-3'>
                    <input type='text' placeholder='Optional' {...input} className='form-control form-control-lg form-control-solid' />
                    <FormError meta={meta} />
                  </div>
                </>
              )}
            </Field>
          </div>

          <div className='row'>
            <div className='mb-5 col-lg-6'>
              <Field name='bvn' type='text' defaultValue={profile?.bvn}>
                {({ input, meta }) => (
                  <>
                    <label className='form-label'>BVN</label>
                    <div className='mb-3'>
                      <input type='text' placeholder='Optional' {...input} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>

            <div className='mb-5 col-lg-6'>
              <Field name='nin' type='text' defaultValue={profile?.nin}>
                {({ input, meta }) => (
                  <>
                    <label className='form-label'>NIN</label>
                    <div className='mb-3'>
                      <input type='text' placeholder='Optional' {...input} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
            </div>
          </div>

          <h3 className='fw-bolder text-dark mb-5 mt-10'>Supporting Documents</h3>
          <div className='row'>
            <div className='mb-5 mt-5 col-lg-6'>
              <label className='form-label'>Proof of Income</label>
              <Field name='income_prove' type='file'>
                {({ input: { onChange, value, ...input }, meta }) => (
                  <>
                    <div className='mb-3'>
                      <input type='file' {...input} onChange={({ target }) => onChange(target.files)} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
              {showIncomeDocument && profile.income_prove && (
                <div className='d-flex justify-content-between align-items-center'>
                  <a className='btn btn-sm btn-light-warning' href={profile.income_prove} target='_blank' rel='noopener noreferrer'>
                    View Document
                  </a>
                  <button type='button' className='btn btn-sm btn-light-danger' onClick={deleteIncomeDocument}>
                    Delete Document
                  </button>
                </div>
              )}
            </div>

            <div className='mb-5 mt-5 col-lg-6'>
              <label className='form-label'>Proof of Business or Employment</label>
              <Field name='work_prove' type='file'>
                {({ input: { onChange, value, ...input }, meta }) => (
                  <>
                    <div className='mb-3'>
                      <input type='file' {...input} onChange={({ target }) => onChange(target.files)} className='form-control form-control-lg form-control-solid' />
                      <FormError meta={meta} />
                    </div>
                  </>
                )}
              </Field>
              {showWorkDocument && profile.work_prove && (
                <div className='d-flex justify-content-between align-items-center'>
                  <a href={profile.work_prove} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-light-warning'>
                    View Document
                  </a>
                  <button type='button' className='btn btn-sm btn-light-danger' onClick={deleteWorkDocument}>
                    Delete Document
                  </button>
                </div>
              )}
            </div>
          </div>

          <button type='submit' className='btn btn-success'>
            {submitting ? <LoadingIndicator /> : 'Save Changes'}
          </button>
        </form>
      )}
    />
  ) : (
    <LoadingIndicator message='' />
  );
};

export default BorrowerUpdateProfile;
